import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import chatService from '../services/chat.service';
import { useSelector } from 'react-redux';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export function ChatProvider({ children }) {
  const token = useSelector((state) => state.auth.auth_token);

  const [chats, setChats] = useState([]);
  const totalUnread = useMemo(() => chats.reduce((sum, chat) => sum + (chat.unread || 0), 0), [chats]);

  const onChatUpdate = useCallback(({ chatId, message }) => {
    setChats((prev) => {
      const updated = [...prev];

      const chatIndex = updated.findIndex((chat) => String(chat._id) === String(chatId));

      if (chatIndex === -1) {
        return prev;
      }

      updated[chatIndex] = {
        ...updated[chatIndex],
        lastMessage: message,
        unread: (updated[chatIndex].unread || 0) + 1,
      };

      return updated;
    });
  }, []);

  useEffect(() => {
    const authToken = token || localStorage.getItem('auth_token');
    if (!authToken) return;

    chatService.connect(authToken);

    const unsubscribeChatList = chatService.onChatUpdate(onChatUpdate);

    return () => {
      unsubscribeChatList();
      chatService.disconnect();
    };
  }, [onChatUpdate, token]);

  const uniqueSetChats = useCallback((newChats = [], replaceExisting = false) => {
    setChats((prev) => {
      const prevById = new Map(prev.map((chat) => [String(chat._id), chat]));
      const incomingById = new Map(newChats.map((chat) => [String(chat._id), chat]));

      const mergeChat = (chat) => {
        const prevChat = prevById.get(String(chat._id));

        return prevChat ? { ...prevChat, ...chat } : chat;
      };

      if (replaceExisting) {
        const nextChats = newChats.map(mergeChat);

        prev.forEach((chat) => {
          if (!incomingById.has(String(chat._id))) {
            nextChats.push(chat);
          }
        });

        return nextChats;
      }

      const nextChats = prev.map((chat) => {
        const incomingChat = incomingById.get(String(chat._id));

        return incomingChat ? { ...chat, ...incomingChat } : chat;
      });

      newChats.forEach((chat) => {
        if (!prevById.has(String(chat._id))) {
          nextChats.push(mergeChat(chat));
        }
      });

      return nextChats;
    });
  }, []);

  const markChatAsRead = useCallback((chatId) => {
    setChats((prev) => {
      let hasChanges = false;

      const nextChats = prev.map((chat) => {
        if (String(chat._id) !== String(chatId) || !chat.unread) {
          return chat;
        }

        hasChanges = true;

        return {
          ...chat,
          unread: 0,
        };
      });

      return hasChanges ? nextChats : prev;
    });
  }, []);

  const deleteChatById = useCallback((chatId) => {
    setChats((prev) => {
      const newChats = prev.filter((c) => c._id.toString() !== chatId.toString());

      return newChats;
    });
  }, []);

  const value = useMemo(
    () => ({ chats, setChats, uniqueSetChats, totalUnread, deleteChatById, markChatAsRead }),
    [chats, uniqueSetChats, totalUnread, deleteChatById, markChatAsRead],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
